import { useEffect, useState } from 'react';
import { useQuery } from 'urql';
import { useWorkspace } from '@/stores/workspace.store';
import { Q_PROJECTS } from '@/lib/queries';
import { useUi } from '@/stores/ui.store';

const ALL_PLATFORMS = [
  { id: 'linkedin',  label: 'LinkedIn',  bg: 'bg-blue-600' },
  { id: 'twitter',   label: 'X / Twitter', bg: 'bg-stone-900' },
  { id: 'facebook',  label: 'Facebook',  bg: 'bg-blue-500' },
  { id: 'instagram', label: 'Instagram', bg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400' },
  { id: 'tiktok',    label: 'TikTok',    bg: 'bg-black' },
  { id: 'youtube',   label: 'YouTube',   bg: 'bg-red-600' },
  { id: 'threads',   label: 'Threads',   bg: 'bg-stone-900' },
  { id: 'pinterest', label: 'Pinterest', bg: 'bg-red-600' },
];

const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

interface Connection { id: string; platform: string; accountLabel: string; status: string }

export default function ConnectionsRoute() {
  const { toast } = useUi();
  const [projects, refetchProjects] = useQuery({ query: Q_PROJECTS });
  const project = (projects.data?.projects?.find((p: any) => p.id === useWorkspace.getState().project_id) ?? projects.data?.projects?.[0]);
  const connections: Connection[] = project?.connections ?? [];
  const [busy, setBusy] = useState<string | null>(null);

  // Handle the OAuth return: /app/connections?connected=<platform>&id=<id>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get('connected');
    if (!connected) return;
    toast(`${connected} connected`, 'success');
    refetchProjects({ requestPolicy: 'network-only' });
    // Strip the query so a refresh doesn't re-toast.
    window.history.replaceState({}, '', location.pathname);
  }, []);

  // Most-recent row per platform — connected rows win over disabled (audit) rows.
  const rowFor = (id: string): Connection | undefined => {
    const rows = connections.filter((c) => c.platform === id);
    return rows.find((c) => c.status === 'connected') ?? rows[0];
  };

  const authHeaders = () => ({
    'X-Workspace-Id': localStorage.getItem('workspace_id') ?? '',
    Authorization: localStorage.getItem('access_token') ? `Bearer ${localStorage.getItem('access_token')}` : '',
  });

  // Kick off OAuth — D1's /start handles replace-on-reconnect (old row → disabled).
  const connect = async (platform: string) => {
    if (!project?.id) { toast('No project found', 'error'); return; }
    setBusy(platform);
    try {
      const res = await fetch(
        `${API_URL}/v1/connections/${platform}/start?project_id=${project.id}&return_to=${encodeURIComponent(location.origin + '/app/connections')}`,
        { headers: authHeaders() },
      );
      if (!res.ok) { toast(`Failed to start ${platform} OAuth`, 'error'); return; }
      const data = await res.json();
      if (data.authorize_url) window.location.href = data.authorize_url;
    } catch (e: any) {
      toast(e?.message ?? 'OAuth start failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  // POST /v1/connections/:id/disconnect → sets status='disabled' (kept for audit).
  const disconnect = async (conn: Connection) => {
    setBusy(conn.platform);
    try {
      const res = await fetch(`${API_URL}/v1/connections/${conn.id}/disconnect`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) { toast('Failed to disconnect', 'error'); return; }
      toast(`${conn.platform} disconnected`);
      refetchProjects({ requestPolicy: 'network-only' });
    } catch (e: any) {
      toast(e?.message ?? 'Disconnect failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  if (projects.fetching) return <div className="p-8 text-stone-500">Loading…</div>;

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-stone-900">Connections</h1>
        <p className="text-sm text-stone-500 mt-1">Link social accounts so the publishing engine can post on your behalf.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ALL_PLATFORMS.map((p) => {
          const conn = rowFor(p.id);
          const connected = conn?.status === 'connected';
          const disabled = conn?.status === 'disabled';   // previously linked, now retired
          const isBusy = busy === p.id;
          return (
            <div key={p.id} className="bg-white border border-stone-200 rounded-xl p-5 flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className={`${p.bg} w-11 h-11 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0`}>{p.label[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-900">{p.label}</p>
                  {connected ? (
                    <p className="text-[11px] text-stone-500 truncate">{conn?.accountLabel}</p>
                  ) : disabled ? (
                    <p className="text-[11px] text-amber-600 truncate">{conn?.accountLabel} · disconnected</p>
                  ) : (
                    <p className="text-[11px] text-stone-400">Not connected</p>
                  )}
                </div>
                {connected && <span className="text-[10px] uppercase font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 flex-shrink-0">connected</span>}
                {disabled && <span className="text-[10px] uppercase font-medium px-2 py-0.5 rounded bg-stone-100 text-stone-500 flex-shrink-0">disabled</span>}
              </div>

              {/* LinkedIn org posting needs admin-approved org scopes (LINKEDIN_ORG_SCOPES). */}
              {p.id === 'linkedin' && connected && (
                <p className="text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
                  Organization (Company Page) posting requires admin-approved org scopes. Personal posting works now; org scope is gated until LinkedIn MDP approval.
                </p>
              )}

              <div className="flex gap-2">
                {connected ? (
                  <>
                    <button disabled={isBusy} onClick={() => disconnect(conn!)} className="flex-1 px-3 py-1.5 text-xs border border-stone-200 text-stone-600 hover:bg-stone-50 rounded font-medium disabled:opacity-50">Disconnect</button>
                    <button disabled={isBusy} onClick={() => connect(p.id)} className="flex-1 px-3 py-1.5 text-xs border border-violet-200 text-violet-700 hover:bg-violet-50 rounded font-medium disabled:opacity-50">Reconnect</button>
                  </>
                ) : (
                  <button disabled={isBusy} onClick={() => connect(p.id)} className="w-full px-3 py-1.5 text-xs bg-violet-600 hover:bg-violet-700 text-white rounded font-medium disabled:opacity-50">
                    {isBusy ? 'Starting…' : disabled ? 'Reconnect account' : 'Connect'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <strong>Demo mode:</strong> no real OAuth apps are configured, so "Connect" links a <em>mock</em> account instantly — enough to test publishing &amp; automation end-to-end.
        To connect real accounts, set each platform's credentials (LINKEDIN_CLIENT_ID, etc.) — see <code>PLATFORM_INTEGRATION.md</code>.
      </div>
    </div>
  );
}
